import { Link } from "@inertiajs/react"
import { ArchiveRestore, Inbox } from "lucide-react"
import { useTranslation } from "react-i18next"

import { EmptyState } from "@/components/empty-state"
import { Button } from "@/components/ui/button"
import { candidatesPath } from "@/routes"

/**
 * OKS-465 — Active view, brand has candidates but ALL of them are
 * removed (UX Spec §3 first bullet + last-candidate edge). Distinct from
 * `CandidateEmptyState` (brand-new, zero candidates ever) — we must NOT
 * show the "add your first candidate" CTA here; instead point the admin
 * to the Removed view.
 *
 * Locked testids:
 *   - `candidate-list-no-active` — section root
 *   - `candidate-list-view-removed-cta` — "View removed (N)" link
 */
export function CandidateAllRemovedEmptyState({
  removedCount,
}: {
  removedCount: number
}) {
  const { t } = useTranslation()
  return (
    <div data-testid="candidate-list-no-active">
      <EmptyState
        icon={Inbox}
        title={t("candidates.list.no_active.title")}
        description={t("candidates.list.no_active.description")}
        actions={
          <Button asChild variant="outline">
            <Link
              href={candidatesPath({ removed: 1 })}
              data-testid="candidate-list-view-removed-cta"
            >
              <ArchiveRestore className="size-4" aria-hidden="true" />
              {t("candidates.list.no_active.view_removed", {
                count: removedCount,
              })}
            </Link>
          </Button>
        }
      />
    </div>
  )
}

/**
 * OKS-465 — Removed view with nothing removed (UX Spec §3 third bullet).
 * Reuses the no-results panel pattern (NOT the add-first CTA) + a link
 * back to the active list.
 *
 * Locked testids:
 *   - `candidate-list-removed-empty` — section root
 *   - `candidate-list-back-to-active-cta` — link back to the active list
 */
export function CandidateRemovedViewEmptyState() {
  const { t } = useTranslation()
  return (
    <div data-testid="candidate-list-removed-empty">
      <EmptyState
        icon={Inbox}
        title={t("candidates.list.removed_empty.title")}
        description={t("candidates.list.removed_empty.description")}
        actions={
          <Button asChild variant="outline">
            <Link
              href={candidatesPath()}
              data-testid="candidate-list-back-to-active-cta"
            >
              {t("candidates.list.removed_empty.back_to_active")}
            </Link>
          </Button>
        }
      />
    </div>
  )
}
